import { CoreData, CytoscapeOptions, EdgeDefinition, ElementsDefinition, LayoutOptions, Stylesheet } from 'cytoscape';

import {
    ELK_OPTIONS,
    SYNOPTIC_CYTOSCAPE_STYLES,
} from '@connect-services/client/components/synoptic/synoptic-graph/graph-core/synoptic-graph.constants';
import {
    AvancementCodeEnum,
    AvancementCssClass,
    Direction,
    GenericSynopticNodeDataDefinition,
    GraceThdSynopticNodeDataDefinition,
    SynopticStyleDefinition,
} from '@connect-services/client/components/synoptic/synoptic-graph/graph-core/synoptic-graph.model';
import { GenericSynopticItem, GraceThdSynopticItem } from '@connect-services/client/sdk/api';

export abstract class SynopticGraphBuilderService<T extends GraceThdSynopticItem | GenericSynopticItem> {
    private readonly avancementClassCssByCode: Record<string, AvancementCssClass> = {
        [AvancementCodeEnum.ToCreate]: AvancementCssClass.ToCreate,
        [AvancementCodeEnum.Maintenance]: AvancementCssClass.Maintenance,
        [AvancementCodeEnum.Working]: AvancementCssClass.Working,
        [AvancementCodeEnum.NotWorking]: AvancementCssClass.NotWorking,
        [AvancementCodeEnum.Discarded]: AvancementCssClass.Discarded,
        [AvancementCodeEnum.Existing]: AvancementCssClass.Existing,
        [AvancementCodeEnum.Unknown]: AvancementCssClass.Unknown,
    };

    private readonly nodeLabelHeight = 17;

    public buildSynoptic(data: T[]): CytoscapeOptions {
        const layout: LayoutOptions = ELK_OPTIONS;
        const elements = this.generateGraph(data);
        const renderNode = this.buildRenderNode();

        const style: Stylesheet[] = SYNOPTIC_CYTOSCAPE_STYLES.map((styleItem: SynopticStyleDefinition): Stylesheet => {
            if (styleItem.selector !== 'node') {
                return styleItem;
            }

            return {
                ...styleItem,
                style: {
                    ...styleItem.style,
                    'background-image': (node: CoreData) => renderNode(node).img,
                    height: (node: CoreData) => renderNode(node).height,
                    width: (node: CoreData) => renderNode(node).width,
                },
            };
        });

        return {
            autounselectify: true,
            boxSelectionEnabled: false,
            elements,
            layout,
            style,
            wheelSensitivity: 0.6,
        };
    }

    protected getElementColorCssClass(color: string): AvancementCssClass {
        return this.avancementClassCssByCode[color] ?? AvancementCssClass.Unknown;
    }

    private buildRenderNode =
        () =>
        (element: CoreData): { height: number; img: string; width: number } => {
            const width = 500;

            let corpus = '';

            const x = width / 2;
            const __ret = this.getRenderNodeLabels(element, corpus, x, this.nodeLabelHeight);
            corpus = __ret.corpus;
            const height = __ret.y;

            let svg = '<?xml version="1.0" encoding="UTF-8"?><!DOCTYPE svg>';
            svg += `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">`;

            svg += `<g>${corpus}</g>`;
            svg += '</svg>';

            const img = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;

            return { height, img, width };
        };

    private generateGraph(data: T[]): ElementsDefinition {
        const nodes: (GenericSynopticNodeDataDefinition | GraceThdSynopticNodeDataDefinition)[] = [];
        const edges: EdgeDefinition[] = [];
        const uniques = new Set<string>();

        data.forEach((item: T): void => {
            if (!uniques.has(item.from)) {
                nodes.push(this.buildNode(item, Direction.From));
                uniques.add(item.from);
            }
            if (!uniques.has(item.to)) {
                nodes.push(this.buildNode(item, Direction.To));
                uniques.add(item.to);
            }

            const edge = this.buildEdge(item);
            edges.push(edge);
        });

        return { edges, nodes };
    }

    protected abstract buildEdge(item: T): EdgeDefinition;

    protected abstract buildNode(
        item: T,
        suffix: Direction,
    ): GenericSynopticNodeDataDefinition | GraceThdSynopticNodeDataDefinition;

    protected abstract getRenderNodeLabels(
        element: cytoscape.CoreData,
        corpus: string,
        x: number,
        y: number,
    ): { corpus: string; y: number };
}
